import { useEffect, useMemo, useState } from 'react'
import { emojiFromEmotion } from '../../../../components/DeviceScreenPreview'
import { normalizeEmotion } from '../../../../utils/emotion'
import { useI18n } from '../../../../utils/i18n'
import { THEMES } from './eyesPreviewCatalog'
import { FoilParallaxCard } from './FoilParallaxCard'
import { ACCENT_PRESETS, ANI_MOOD_CARDS, DEFAULT_VARIANT_BY_THEME, EMOTIONS,
    ESP32_EXPRESSION_CARDS, LIBRARY_FAMILY_LABELS, LIBRARY_MOODS,
    MIAOBAN_MOOD_CARDS, OSS_PROJECTS, clamp01, groupOssProjects,
    libraryFamilyKey, ossGroupDisplayName, ossGroupKey, repoNameFromUrl,
    roboCardForKey, roboGroupKey, shortenTag, summarizeList } from './eyesPreviewData'

export function useEyesPreview() {
  const { tx } = useI18n()
  const auditMode = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('audit') === '1'
    } catch {
      return false
    }
  }, [])
  const [view, setView] = useState(() => {
    if (auditMode) return 'compare'
    try {
      const q = new URLSearchParams(window.location.search).get('view')
      if (q === 'library' || q === 'compare') return q
    } catch {
      // ignore
    }
    return localStorage.getItem('eyes.preview.view') || 'library'
  })
  const [theme, setTheme] = useState(() => localStorage.getItem('eyes.preview.theme') || 'robo')
  const [variant, setVariant] = useState(() => localStorage.getItem('eyes.preview.variant') || '')
  const [accent, setAccent] = useState(() => localStorage.getItem('eyes.preview.accent') || '#fb923c')
  const [libraryEmotion, setLibraryEmotion] = useState(() => localStorage.getItem('eyes.library.emotion') || 'happy')
  const [libraryQuery, setLibraryQuery] = useState(() => localStorage.getItem('eyes.library.query') || '')
  const [libraryGroupSimilar, setLibraryGroupSimilar] = useState(() => (localStorage.getItem('eyes.library.groupSimilar') || '1') !== '0')
  const [detailsKey, setDetailsKey] = useState(null)
  const [detailsSubKey, setDetailsSubKey] = useState(null)
  const [poke, setPoke] = useState(0)
  const [scale, setScale] = useState(() => Number(localStorage.getItem('eyes.preview.scale') || 1))

  useEffect(() => {
    if (auditMode && view !== 'compare') setView('compare')
  }, [auditMode, view])

  const cardScale = useMemo(() => (Number.isFinite(scale) ? clamp01(scale) : 1), [scale])
  const selectedProjects = useMemo(
    () => OSS_PROJECTS.filter((p) => p.theme === theme && (p.variant || '') === (variant || '')),
    [theme, variant],
  )
  const selectedProject = selectedProjects[0] || null
  const ossGroups = useMemo(() => groupOssProjects(OSS_PROJECTS, THEMES), [])
  const themeLabelById = useMemo(() => new Map(THEMES.map((t) => [t.id, t.label])), [])
  const detailsProduct = useMemo(() => {
    if (!detailsKey) return null
    const family = ossGroups.filter((g) => libraryFamilyKey(g) === detailsKey)
    if (family.length) return { key: detailsKey, groups: family }
    const solo = ossGroups.find((g) => ossGroupKey(g) === detailsKey)
    return solo ? { key: detailsKey, groups: [solo] } : null
  }, [detailsKey, ossGroups])
  const detailsActiveGroup = useMemo(() => {
    const groups = detailsProduct?.groups || []
    if (!groups.length) return null
    if (detailsSubKey) {
      const hit = groups.find((g) => ossGroupKey(g) === detailsSubKey)
      if (hit) return hit
    }
    return groups[0]
  }, [detailsProduct, detailsSubKey])
  const auditGroupIndex = useMemo(() => {
    const key = `${theme}::${variant || ''}`
    return ossGroups.findIndex((g) => `${g.theme}::${g.variant || ''}` === key)
  }, [ossGroups, theme, variant])

  const cards = useMemo(() => {
    if (auditMode) {
      if (theme === 'ani') {
        const pick = new Set(['Happy', 'Neutral', 'Sleep', 'Angry'])
        return ANI_MOOD_CARDS.filter((c) => pick.has(c.id))
      }
      if (theme === 'esp32') {
        // Prefer emotion-based mapping (variant affects preset mapping).
        return ['neutral', 'sad', 'angry', 'surprised'].map((e) => ({
          id: e,
          emoji: emojiFromEmotion(e),
          subtitle: e,
          emotion: e,
        }))
      }
      return ['neutral', 'happy', 'angry', 'sleepy'].map((e) => ({
        id: e,
        emoji: emojiFromEmotion(e),
        subtitle: e,
        emotion: e,
      }))
    }
    if (theme === 'ani') return ANI_MOOD_CARDS
    if (theme === 'esp32') return ESP32_EXPRESSION_CARDS
    if (theme === 'miaoban') return MIAOBAN_MOOD_CARDS

    const all = EMOTIONS.map((raw) => normalizeEmotion(raw))

    // RoboEyes only supports a few moods + a couple one-shot animations.
    // Showing all emotions will create many duplicates that look identical.
    if (theme === 'robo') {
      const groups = new Map()
      const order = []
      for (const emo of all) {
        const k = roboGroupKey(emo)
        if (!groups.has(k)) {
          groups.set(k, [])
          order.push(k)
        }
        groups.get(k).push(emo)
      }
      return order.map((k) => {
        const base = roboCardForKey(k)
        const mapped = groups.get(k) || []
        return {
          ...base,
          subtitle: `${base.subtitle} · mapped: ${summarizeList(mapped)}`,
        }
      })
    }

    // RawEyes only implements a small subset of emotions. Others will look identical (fallback).
    if (theme === 'raw') {
      const keyFor = (e) => (['happy', 'angry', 'sleepy', 'surprised'].includes(e) ? e : 'default')
      const groups = new Map()
      const order = []
      for (const e of all) {
        const k = keyFor(e)
        if (!groups.has(k)) {
          groups.set(k, [])
          order.push(k)
        }
        groups.get(k).push(e)
      }
      return order.map((k) => {
        const mapped = groups.get(k) || []
        if (k === 'default') {
          return {
            id: 'default',
            label: { zh: '默认（映射）', en: 'Default (mapped)' },
            emoji: emojiFromEmotion('neutral'),
            subtitle: `mapped: ${summarizeList(mapped)}`,
            emotion: 'neutral',
          }
        }
        return {
          id: k,
          emoji: emojiFromEmotion(k),
          subtitle: k,
          emotion: k,
        }
      })
    }

    // OLED Iris/Soft variants only react to a subset of emotions; dedupe the rest.
    const oledCompactVariants = [
      'iris',
      'ir_basic',
      'iris_ring',
      'iris_cat',
      'iris_star',
      'ojos_pro',
      'dragon',
      'soft_mono',
      'soft_thick',
      's3',
      'lenny',
    ]
    if (theme === 'oled' && oledCompactVariants.includes(variant || '')) {
      const keyFor = (e) => (['sleepy', 'angry', 'sad', 'surprised', 'loving'].includes(e) ? e : 'default')
      const groups = new Map()
      const order = []
      for (const e of all) {
        const k = keyFor(e)
        if (!groups.has(k)) {
          groups.set(k, [])
          order.push(k)
        }
        groups.get(k).push(e)
      }
      return order.map((k) => {
        const mapped = groups.get(k) || []
        if (k === 'default') {
          return {
            id: 'default',
            label: { zh: '默认（映射）', en: 'Default (mapped)' },
            emoji: emojiFromEmotion('neutral'),
            subtitle: `mapped: ${summarizeList(mapped)}`,
            emotion: 'neutral',
          }
        }
        return {
          id: k,
          emoji: emojiFromEmotion(k),
          subtitle: k,
          emotion: k,
        }
      })
    }

    return all.map((e) => ({
      id: e,
      emoji: emojiFromEmotion(e),
      subtitle: e,
      emotion: e,
    }))
  }, [auditMode, theme, variant])

  const selectTheme = (t) => {
    setTheme(t)
    localStorage.setItem('eyes.preview.theme', t)
    const groups = (ossGroups || []).filter((g) => g.theme === t)
    const preferredVariant = DEFAULT_VARIANT_BY_THEME[t] ?? ''
    const pick = groups.find((g) => (g.variant || '') === preferredVariant) || groups.find((g) => (g.variant || '') === '') || groups[0]
    const nextVariant = pick?.variant || ''
    setVariant(nextVariant)
    localStorage.setItem('eyes.preview.variant', nextVariant)
  }

  const selectOssGroup = (g) => {
    setTheme(g.theme)
    localStorage.setItem('eyes.preview.theme', g.theme)
    setVariant(g.variant || '')
    localStorage.setItem('eyes.preview.variant', g.variant || '')
  }

  const stepAuditGroup = (delta) => {
    if (!auditMode) return
    if (!ossGroups.length) return
    const idx = auditGroupIndex >= 0 ? auditGroupIndex : 0
    const next = (idx + delta + ossGroups.length) % ossGroups.length
    selectOssGroup(ossGroups[next])
  }

  const updateView = (next) => {
    setView(next)
    localStorage.setItem('eyes.preview.view', next)
  }

  const updateAccent = (v) => {
    setAccent(v)
    localStorage.setItem('eyes.preview.accent', v)
  }

  const updateLibraryEmotion = (v) => {
    const next = normalizeEmotion(v)
    setLibraryEmotion(next)
    localStorage.setItem('eyes.library.emotion', next)
  }

  const updateLibraryQuery = (v) => {
    setLibraryQuery(v)
    localStorage.setItem('eyes.library.query', v)
  }

  const updateLibraryGroupSimilar = (v) => {
    setLibraryGroupSimilar(v)
    localStorage.setItem('eyes.library.groupSimilar', v ? '1' : '0')
  }

  const updateScale = (v) => {
    const n = Number(v)
    setScale(n)
    localStorage.setItem('eyes.preview.scale', String(n))
  }

  const inLibrary = !auditMode && view === 'library'
  const libraryFilteredGroups = useMemo(() => {
    const q = String(libraryQuery || '').trim().toLowerCase()
    if (!q) return ossGroups
    const tokens = q.split(/\s+/).filter(Boolean)
    return ossGroups.filter((g) => {
      const primary = g.projects?.find((p) => p?.tag) || g.projects?.[0]
      const hay = [
        g.label,
        g.theme,
        g.variant,
        ossGroupDisplayName(g),
        primary?.tag,
        primary?.label,
        primary?.repo,
        ...(g.projects || []).flatMap((p) => [p?.tag, p?.label, p?.repo, repoNameFromUrl(p?.repo)]),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return tokens.every((t) => hay.includes(t))
    })
  }, [libraryQuery, ossGroups])

  const libraryProducts = useMemo(() => {
    if (!inLibrary) return []
    const groups = libraryFilteredGroups

    const makeSingle = (g) => {
      const familyKey = libraryFamilyKey(g)
      return {
        key: ossGroupKey(g),
        groups: [g],
        detailsKey: familyKey.endsWith('::family') ? familyKey : undefined,
      }
    }

    if (!libraryGroupSimilar) return groups.map(makeSingle)

    const result = []
    const map = new Map()
    for (const g of groups) {
      const familyKey = libraryFamilyKey(g)
      if (!map.has(familyKey)) {
        const product = { key: familyKey, groups: [], detailsKey: familyKey.endsWith('::family') ? familyKey : undefined }
        map.set(familyKey, product)
        result.push(product)
      }
      map.get(familyKey).groups.push(g)
    }

    return result.map((p) => {
      const ordered = [...(p.groups || [])]
      ordered.sort((a, b) => String(ossGroupDisplayName(a)).localeCompare(String(ossGroupDisplayName(b))))
      return { ...p, groups: ordered }
    })
  }, [inLibrary, libraryFilteredGroups, libraryGroupSimilar])

  const pinnedLibraryProducts = useMemo(() => {
    if (!inLibrary) return []
    const build = (familyKey) => {
      const groups = (ossGroups || []).filter((g) => libraryFamilyKey(g) === familyKey)
      if (!groups.length) return null
      return { key: familyKey, groups, detailsKey: familyKey.endsWith('::family') ? familyKey : undefined }
    }
    const robo = build('robo::')
    const chamii = build('chamii::family')
    const miaoban = build('miaoban::')
    return [robo, chamii, miaoban].filter(Boolean)
  }, [inLibrary, ossGroups])

  const pinnedLibraryKeySet = useMemo(() => new Set((pinnedLibraryProducts || []).map((p) => p.key)), [pinnedLibraryProducts])
  const extraLibraryProducts = useMemo(
    () => (inLibrary ? (libraryProducts || []).filter((p) => !pinnedLibraryKeySet.has(p.key)) : []),
    [inLibrary, libraryProducts, pinnedLibraryKeySet],
  )

  const openDetails = (product) => {
    const g0 = product?.groups?.[0]
    if (!g0) return
    setDetailsKey(product.detailsKey || product.key)
    setDetailsSubKey(ossGroupKey(g0))
    selectOssGroup(g0)
  }

  const openCompareFromModal = () => {
    updateView('compare')
    setDetailsKey(null)
    setDetailsSubKey(null)
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {
      window.scrollTo(0, 0)
    }
  }

  const renderLibraryProduct = (product) => {
    const groups = product?.groups || []
    const primary = groups[0]
    if (!primary) return null
    const repoCount = groups.reduce((acc, g) => acc + (Array.isArray(g.projects) ? g.projects.length : 0), 0)
    const styleCount = groups.length
    const baseTitle = tx(LIBRARY_FAMILY_LABELS[product.key] || ossGroupDisplayName(primary))
    const label = tx(themeLabelById.get(primary.theme) || primary.theme)
    const title = primary.theme === 'chamii' && product.detailsKey === 'chamii::family' ? `${label} · ${baseTitle}` : baseTitle
    return (
      <FoilParallaxCard
        key={product.key}
        className="border-2 border-slate-50 bg-white dark:border-slate-800 dark:bg-slate-950"
      >
        <button type="button" onClick={() => openDetails(product)} className="w-full text-left">
          <div className="px-5 py-4 border-b-2 border-brand-textDark/10 flex items-center justify-between gap-3 dark:border-slate-800">
            <div className="min-w-0">
              <div className="text-brand-textDark dark:text-slate-100 font-extrabold truncate">{title}</div>
              <div className="text-brand-text dark:text-slate-300 text-xs font-bold truncate">
                {label}
                {styleCount === 1 && primary.variant ? ` · ${primary.variant}` : styleCount > 1 ? ` · ${styleCount} styles` : ''}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {repoCount > 1 ? (
                <span className="px-2 py-1 rounded-full text-[10px] font-black tracking-widest bg-brand-bg text-brand-textDark/80 border border-brand-textDark/10 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700">
                  ×{repoCount}
                </span>
              ) : null}
              <span className="px-2 py-1 rounded-full text-[10px] font-black tracking-widest bg-brand-textDark text-white border border-brand-textDark dark:bg-slate-100 dark:text-slate-900 dark:border-slate-100">
                {styleCount > 1 ? primary.theme : primary.variant ? `${primary.theme}:${primary.variant}` : primary.theme}
              </span>
            </div>
          </div>
          <div className="p-4 flex justify-center">
            <div style={{ transform: `scale(${cardScale})`, transformOrigin: 'top center' }}>
              <DeviceScreenPreview
                theme={primary.theme}
                variant={primary.variant || undefined}
                emotion={libraryEmotion}
                accent={accent}
                poke={poke}
                contentOnly
              />
            </div>
          </div>
        </button>
      </FoilParallaxCard>
    )
  }


  return { accent, auditGroupIndex, auditMode, cardScale, cards, detailsActiveGroup,
    detailsProduct, extraLibraryProducts, inLibrary, libraryEmotion,
    libraryFilteredGroups, libraryGroupSimilar, libraryQuery, openCompareFromModal,
    ossGroups, pinnedLibraryProducts, poke, renderLibraryProduct, selectOssGroup,
    selectTheme, selectedProject, selectedProjects, setDetailsKey,
    setDetailsSubKey, setPoke, stepAuditGroup, theme, themeLabelById, tx,
    updateAccent, updateLibraryEmotion, updateLibraryGroupSimilar,
    updateLibraryQuery, updateScale, updateView, variant, view }
}
